// Labelled checkbox with description. Plan 24.
//
// A stacked `<label>` wraps the input so clicking anywhere in the card
// toggles it. We autogenerate an id via `useId()` so multiple instances
// on the same form don't collide; callers can still override with `id`.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface FormCheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'type'
> {
  label: ReactNode;
  description?: ReactNode;
  id?: string;
  containerClassName?: string;
}

export const FormCheckbox = forwardRef<HTMLInputElement, FormCheckboxProps>(function FormCheckbox(
  { label, description, id, className, containerClassName, ...rest },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const descriptionId = description ? `${inputId}-desc` : undefined;

  return (
    <label
      htmlFor={inputId}
      className={cn('flex items-start gap-3 cursor-pointer select-none', containerClassName)}
    >
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        aria-describedby={descriptionId}
        className={cn(
          'mt-1 w-4 h-4 rounded border-stone-300 text-stone-900 focus-visible:ring-2 focus-visible:ring-stone-900/20',
          className
        )}
        {...rest}
      />
      <span className="flex-1">
        <span className="text-sm font-medium text-stone-900 block">{label}</span>
        {description && (
          <span id={descriptionId} className="text-xs text-stone-500 leading-relaxed block mt-1">
            {description}
          </span>
        )}
      </span>
    </label>
  );
});
